import { isToday as isTodayDateFns, format, isSameDay as isSameDayFns, formatDistanceToNow, parseISO } from "date-fns";
import i18next from "i18next";

export function dateToFromNowDaily(myDate: Date) {
  const today = new Date();
  if (isSameDayFns(today, myDate)) return "Today";

  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (isSameDayFns(tomorrow, myDate)) return "Tomorrow";

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDayFns(yesterday, myDate)) return "Yesterday";

  return format(myDate, "dd/MM/yyyy");
}
 
export function isSameDay({ date1, date2 }: { date1: Date; date2: Date }) {
  return isSameDayFns(date1, date2);
}
 
export function getTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
 
export function getLocalDateAndTimezone(dateUtc: Date | number) {
  const localDate = new Date(dateUtc);
  return { date: localDate.toISOString().split(".")[0], timezone: getTimezone() };
}

export function isToday(timestamp: number): boolean {
  return isTodayDateFns(timestamp);
}

export function getCurrentTimeForTimepicker(date: Date | number | string) {
  const now = new Date(date);
  return { hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds() } as any;
}
 
export function createDateFromTimepicker({
  hour,
  minute,
  second,
}: {
  hour: number;
  minute: number;
  second: number;
}) {
  const date = new Date();
  date.setHours(hour);
  date.setMinutes(minute);
  date.setSeconds(second);

  return date;
}
export function calculateTimeAgo(createdAt: string) {
  const currentDate = new Date().getTime();
  const createdDate = new Date(createdAt).getTime();

  const diff = currentDate - createdDate;

  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(diff / (1000 * 60));
  const hours = Math.round(diff / (1000 * 60 * 60));
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.round(diff / (1000 * 60 * 60 * 24 * 7));
  const months = Math.round(diff / (1000 * 60 * 60 * 24 * 30));
  const years = Math.round(diff / (1000 * 60 * 60 * 24 * 365));

  if (years > 0) {
    return `Posted ${years} year${years > 1 ? "s" : ""} ago`;
  } else if (months > 0) {
    return `Posted ${months} month${months > 1 ? "s" : ""} ago`;
  } else if (weeks > 0) {
    return `Posted ${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (days > 1) {
    return `Posted ${days} days ago`;
  } else if (days === 1) {
    return "Posted one day ago";
  } else if (hours > 0) {
    return `Posted ${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (minutes > 0) {
    return `Posted ${minutes} min${minutes > 1 ? "s" : ""} ago`;
  } else if (seconds >= 0) {
    return "Posted just now";
  }
}

export const formatDate = (inputDateStr) => {
  const inputDate = new Date(inputDateStr);

  if (isNaN(inputDate.getTime())) {
    return "Invalid Date";
  }
 
  return format(inputDate, "MM/dd/yyyy");
};

export const getDayWithSuffix = (day) => {
  if (day > 3 && day < 21) return day + "th";
  switch (day % 10) {
    case 1:
      return day + "st";
    case 2:
      return day + "nd";
    case 3:
      return day + "rd";
    default:
      return day + "th";
  }
};

const normalizeRemainingTimeMs = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }

  const now = Date.now();

  // Some callers pass the absolute expiration timestamp instead of the
  // remaining duration. Normalize both shapes into milliseconds left.
  return value > now ? value - now : value;
};

export const formatRemainingTime = (msLeft: number, isCancelled = false, isOnTrial = false) => {
  const MS_IN_A_DAY = 24 * 60 * 60 * 1000;
  const remainingMs = normalizeRemainingTimeMs(msLeft);
  const days = Math.floor(remainingMs / MS_IN_A_DAY);

  const dayPlural = days > 1 ? i18next.t("subscription.days") : i18next.t("subscription.day");

  if (isOnTrial) {
    if (isCancelled) {
      return days > 1
        ? i18next.t("subscription.cancels_in", { days, day_plural: dayPlural })
        : i18next.t("subscription.cancels_today");
    }
    return days > 1
      ? i18next.t("subscription.trial_expires_in", { days })
      : i18next.t("subscription.trial_expires_today");
  }

  if (days > 0) {
    return isCancelled
      ? i18next.t("subscription.cancels_in", { days, day_plural: dayPlural })
      : i18next.t("subscription.renews_in", { days, day_plural: dayPlural });
  } else {
    return isCancelled
      ? i18next.t("subscription.cancels_today")
      : i18next.t("subscription.renews_today");
  }
};

export const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes}m ${seconds}s`;
};


export const formatDateToYYYYMMDD = (date: Date | string | null | undefined) => {
  if (!date) return null;

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return null;
  }

  return format(dateObj, "yyyy-MM-dd");
};

export const formatTimestamp = (date: Date) => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);

  // Handle negative time difference (future dates or invalid dates)
  if (diffInMs < 0) {
    return 'Just now';
  }

  if (diffInHours < 1) {
    const mins = Math.floor(diffInMs / (1000 * 60));
    return mins <= 0 ? 'Just now' : `${mins}m ago`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else {
    return `${Math.floor(diffInHours / 24)}d ago`;
  }
};
